/**
 * Counts the trust strip numbers up to their real value the first time the strip scrolls into view.
 *
 * The values are free text typed in the theme editor ("12.000+", "14 giorni", "94%"), so only the first
 * number in each is animated and everything around it is left alone. The number is reformatted for the
 * page's own language as it climbs, so a thousands separator stays a separator all the way up.
 *
 * Like the rest of the home page animation this runs only when `sustena-reveal` is on <html>, which the
 * head sets when the visitor has not asked for reduced motion. Without it the real numbers, already in
 * the markup, simply stay on screen.
 */
(() => {
  const root = document.documentElement;
  if (!root.classList.contains('sustena-reveal')) return;

  const DURATION = 1600;
  const locale = root.lang || 'it';

  /**
   * @param {string} text
   * @returns {{prefix: string, suffix: string, value: number, format: Intl.NumberFormat} | null}
   */
  const parse = (text) => {
    const match = text.match(/[0-9][0-9.,]*/);
    if (!match || match.index === undefined) return null;

    const raw = match[0];
    const decimals = (raw.split(',')[1] ?? '').length;
    const value = Number(raw.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(value)) return null;

    return {
      prefix: text.slice(0, match.index),
      suffix: text.slice(match.index + raw.length),
      value,
      format: new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        // a number written without a separator keeps its own shape, e.g. a year
        useGrouping: /\d\.\d{3}/.test(raw),
      }),
    };
  };

  /** @param {HTMLElement} element */
  const start = (element) => {
    const target = element.sustenaCount;
    if (!target) return;

    const first = performance.now();
    const step = (now) => {
      const progress = Math.min((now - first) / DURATION, 1);
      // slows into the real number rather than stopping dead on it
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = progress === 1 ? target.value : target.value * eased;
      element.textContent = target.prefix + target.format.format(current) + target.suffix;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        start(/** @type {HTMLElement} */ (entry.target));
      }
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  /** @param {ParentNode} scope */
  const prepare = (scope) => {
    for (const element of scope.querySelectorAll('[data-sustena-count]')) {
      if (!(element instanceof HTMLElement) || element.sustenaCount) continue;

      const target = parse(element.textContent ?? '');
      if (!target) continue;

      element.sustenaCount = target;
      // The strip is still faded out at this point, so starting from zero is never seen
      element.textContent = target.prefix + target.format.format(0) + target.suffix;
      observer.observe(element);
    }
  };

  prepare(document);

  // Theme editor: sections are re-rendered on every change
  document.addEventListener('shopify:section:load', (event) => prepare(/** @type {Element} */ (event.target)));
})();
